import {
  IChargeUnit,
  ISeasonalCharge,
  ITouRate,
  ISeasonalNum,
} from './algoInterface'

/**
 * 단가 계산 - 비율과 최저요금으로 계절별 부하별 단가 계산
 * @param {ITouRate} touRate - 계절별 최저/중간/최대부하 비율
 * @param {ISeasonalNum} minCharge - 계절별 최저요금
 * @returns {ISeasonalCharge} 부하별 요금 단가
 */
export const calTouUnitCharge = (
  touRate: ITouRate,
  minCharge: ISeasonalNum
): ISeasonalCharge => {
  const touUnitCharge: ISeasonalCharge = {
    spr: { min: 0, mid: 0, max: 0 },
    sum: { min: 0, mid: 0, max: 0 },
    fall: { min: 0, mid: 0, max: 0 },
    win: { min: 0, mid: 0, max: 0 },
  }

  // 소숫점 첫째자리까지 (둘째자리에서 올림)

  touUnitCharge.spr.min = minCharge.spr
  touUnitCharge.spr.mid = Number(
    (minCharge.spr * touRate.spr.minMid).toFixed(2)
  )
  touUnitCharge.spr.max = Number(
    (minCharge.spr * touRate.spr.minMax).toFixed(2)
  )

  touUnitCharge.sum.min = minCharge.sum
  touUnitCharge.sum.mid = Number(
    (minCharge.sum * touRate.sum.minMid).toFixed(2)
  )
  touUnitCharge.sum.max = Number(
    (minCharge.sum * touRate.sum.minMax).toFixed(2)
  )

  touUnitCharge.fall.min = minCharge.fall
  touUnitCharge.fall.mid = Number(
    (minCharge.fall * touRate.fall.minMid).toFixed(2)
  )
  touUnitCharge.fall.max = Number(
    (minCharge.fall * touRate.fall.minMax).toFixed(2)
  )

  touUnitCharge.win.min = minCharge.win
  touUnitCharge.win.mid = Number(
    (minCharge.win * touRate.win.minMid).toFixed(2)
  )
  touUnitCharge.win.max = Number(
    (minCharge.win * touRate.win.minMax).toFixed(2)
  )

  return touUnitCharge
}

/**
 * 단가 계산 - 계절별 최소 요금 단가 구하기
 * 수익 = 최저부하*x+중간부하*비율*x+최대부하*비율*x 인 x 값 구하기 (x는 올림)
 * @param {ITouRate} touRate - 계절별 최저/중간/최대부하 가격비율
 * @param {ISeasonalCharge} seasonalCharge - 계절별 최저/중간/최대부하
 * @param {ISeasonalNum} seasonalProfit - 계절별 수익
 * @returns {minUnit} 계절별 최소 요금 단가
 */
export const calMinChargeUnit = (
  touRate: ITouRate,
  seasonalCharge: ISeasonalCharge,
  seasonalProfit: ISeasonalNum
): ISeasonalNum => {
  const seasonalMinCharge: ISeasonalNum = {
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  }

  // (최저부하+중간부하*비율+최대부하*비율) 구하기
  const sprLoad =
    seasonalCharge.spr.min +
    seasonalCharge.spr.mid * touRate.spr.minMid +
    seasonalCharge.spr.max * touRate.spr.minMax
  const sumLoad =
    seasonalCharge.sum.min +
    seasonalCharge.sum.mid * touRate.sum.minMid +
    seasonalCharge.sum.max * touRate.sum.minMax
  const fallLoad =
    seasonalCharge.fall.min +
    seasonalCharge.fall.mid * touRate.fall.minMid +
    seasonalCharge.fall.max * touRate.fall.minMax
  const winLoad =
    seasonalCharge.win.min +
    seasonalCharge.win.mid * touRate.win.minMid +
    seasonalCharge.win.max * touRate.win.minMax

  // x = 수익 / (최저부하+중간부하*비율+최대부하*비율), 소숫점 첫째자리까지 (둘째자리에서 올림)

  seasonalMinCharge.spr = Number((seasonalProfit.spr / sprLoad).toFixed(2))
  seasonalMinCharge.sum = Number((seasonalProfit.sum / sumLoad).toFixed(2))
  seasonalMinCharge.fall = Number((seasonalProfit.fall / fallLoad).toFixed(2))
  seasonalMinCharge.win = Number((seasonalProfit.win / winLoad).toFixed(2))

  return seasonalMinCharge
}

/**
 * 완전 단일 요금제 계산 - 모든 계절/시간대에 동일 단가 적용
 * 연간 총 목표수익 / 연간 총 발전량으로 단일 단가를 계산한다.
 */
export const calFlatTouUnitCharge = (
  seasonalCharge: ISeasonalCharge,
  seasonalProfit: ISeasonalNum
): IChargeUnit => {
  const totalLoad =
    seasonalCharge.spr.min +
    seasonalCharge.spr.mid +
    seasonalCharge.spr.max +
    seasonalCharge.sum.min +
    seasonalCharge.sum.mid +
    seasonalCharge.sum.max +
    seasonalCharge.fall.min +
    seasonalCharge.fall.mid +
    seasonalCharge.fall.max +
    seasonalCharge.win.min +
    seasonalCharge.win.mid +
    seasonalCharge.win.max

  const totalProfit =
    seasonalProfit.spr +
    seasonalProfit.sum +
    seasonalProfit.fall +
    seasonalProfit.win

  const flatUnit = totalLoad === 0 ? 0 : Number((totalProfit / totalLoad).toFixed(2))

  return {
    spr: { min: flatUnit, mid: flatUnit, max: flatUnit },
    sum: { min: flatUnit, mid: flatUnit, max: flatUnit },
    fall: { min: flatUnit, mid: flatUnit, max: flatUnit },
    win: { min: flatUnit, mid: flatUnit, max: flatUnit },
  }
}

/**
 * ( 망요금(없을시 0) + PV 구입비용 ) < < 한전 기존 요금
 * 요금제 결정할 상수 구하기
 * @param {ISeasonalNum} seasonalCharge - 계절별 한전 기존 요금
 * @param {ISeasonalNum} seasonalGridUsageCharge - 계절별 망요금
 * @param {ISeasonalNum} seasonalPVCharge - 계절별 PV 구입비용
 * @param {number} ESSrate- ESS 사업자쪽 비율 (0~1 사이)
 * @returns {ISeasonalNum} 계절별 수익 (요금제 결정)
 */
export const calSeasonalProfit = (
  seasonalCharge: ISeasonalNum,
  seasonalGridUsageCharge: ISeasonalNum,
  seasonalPVCharge: ISeasonalNum,
  ESSrate: number
): ISeasonalNum => {
  const seasonalProfit: ISeasonalNum = {
    spr: 0,
    sum: 0,
    fall: 0,
    win: 0,
  }

  // a < < b 에서 사이를 비율만큼 나눈값은
  // a + (b-a) * 비율

  // a = 망요금 + PV 구입비용
  const sprMin = seasonalGridUsageCharge.spr + seasonalPVCharge.spr
  const sumMin = seasonalGridUsageCharge.sum + seasonalPVCharge.sum
  const fallMin = seasonalGridUsageCharge.fall + seasonalPVCharge.fall
  const winMin = seasonalGridUsageCharge.win + seasonalPVCharge.win

  seasonalProfit.spr = sprMin + (seasonalCharge.spr - sprMin) * ESSrate
  seasonalProfit.sum = sumMin + (seasonalCharge.sum - sumMin) * ESSrate
  seasonalProfit.fall = fallMin + (seasonalCharge.fall - fallMin) * ESSrate
  seasonalProfit.win = winMin + (seasonalCharge.win - winMin) * ESSrate

  return seasonalProfit
}
